import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useQuota } from '@/hooks/useQuota';
import { cn } from '@/utils/cn';

/** Quota key of the monthly blog post allowance (first release counts; drafts and updates are free). */
const BLOG_QUOTA_KEY = 'blog_posts';

interface BlogQuotaUsageLineProps {
  className?: string;
}

/**
 * "7 of 10 blog posts used this month, resets Dec 1". Renders nothing unless limits are enforced, while loading, for unlimited packages
 * or when the backend does not report the quota. Turns amber at the limit.
 */
export const BlogQuotaUsageLine = ({ className }: BlogQuotaUsageLineProps) => {
  const { t } = useTranslation('common');
  const { formatDateOnly } = useTimezone();
  const { hasLimit, used, limit, resetsAt, isAtLimit, enforced } = useQuota(BLOG_QUOTA_KEY);

  // Limits are reported in every mode; customers only see them once the backend enforces
  if (!enforced || !hasLimit || limit === null) return null;

  const text = resetsAt
    ? t('blog_quota_usage_resets', { used, limit, date: formatDateOnly(resetsAt) })
    : t('blog_quota_usage', { used, limit });

  return (
    <p
      data-testid="blog-quota-usage"
      role="status"
      className={cn(
        'flex items-start gap-1.5 text-xs',
        isAtLimit ? 'text-amber-700' : 'text-gray-500',
        className
      )}
    >
      <Icon
        name={isAtLimit ? ICONS.WARNING : ICONS.INFO}
        size="xs"
        color={isAtLimit ? 'text-amber-600' : 'text-gray-400'}
        className="mt-0.5 shrink-0"
        ariaHidden
      />
      <span>{text}</span>
    </p>
  );
};

export default BlogQuotaUsageLine;
